import {
  CARE_TOPICS,
  type CareThread,
  type CareTopic,
  HARD_MOODS,
  MOODS,
  type Mood,
  type ViewContext,
  addDays,
  canJoinMeeting,
  dayMonth,
  shortDate,
  timeOf,
  todayIso,
  useBookCareMeeting,
  useCareActions,
  useCareMeetingSlots,
  useCareThreads,
  useIsOnline,
  useMeetingActions,
  useMeetings,
  useMoodHistory,
  useSetMood,
} from '@shkola/api';
import { LifebuoyIcon } from '@phosphor-icons/react/Lifebuoy';
import { LockSimpleIcon } from '@phosphor-icons/react/LockSimple';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, Modal, Spinner } from '../components/ui';
import {
  Block,
  EventArt,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  TaskArt,
  grad,
  pillClass,
  ringButton,
} from '../design/forms';
import {
  ArrowUpRightIcon,
  CaretRightIcon,
  PaperPlaneTiltIcon,
  VideoCameraIcon,
  WindIcon,
} from '../design/icons';
import { MoodArt } from '../student/home/LineArt';
import { weekdayShort } from '../student/home/model';
import {
  Breathing,
  Choice,
  FIELD,
  Grounding,
  HelpLines,
  IconDot,
  Private,
  ThreadView,
  threadLook,
  threadTitle,
} from './shared';

/*
 * «Турбота» of the student in «Форми 2» (28.10). The promise first, under the title, with a lock: no one
 * but the psychologist sees. Then the ways to get help — «Мені погано зараз» on coral across the top (the
 * help lines are a tap away), the four others as soft cards with their pictures big and quiet behind —
 * and beside them «Як ти сьогодні?» with the faces of the home and the last two weeks of them. Under it
 * the conversations with the psychologist: the list and the open one in one block.
 */

type StudentContext = Extract<ViewContext, { kind: 'student' }>;
type Dialog = 'write' | 'bullying' | 'sos' | 'calm' | 'meet' | 'reveal' | null;

const BULLY_PLACES = [
  'class',
  'corridor',
  'canteen',
  'yard',
  'online',
  'road',
  'elsewhere',
] as const;

/** A label over a choice in a form. */
function Label({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[14px] font-medium" style={{ color: INK }}>
      {children}
    </p>
  );
}

/** «Як ти сьогодні?»: one tap; a hard day gets a gentle offer, never a lecture. */
function MoodCard({
  ctx,
  onCalm,
  onWrite,
}: {
  ctx: StudentContext;
  onCalm: () => void;
  onWrite: () => void;
}) {
  const { t } = useTranslation();
  const history = useMoodHistory(ctx.child.memberId);
  const setMood = useSetMood();
  const online = useIsOnline();
  const today = todayIso();
  const [picked, setPicked] = useState<Mood>();
  const saved = history.data?.find((d) => d.day === today)?.mood;
  const mood = picked ?? saved;
  const days = Array.from({ length: 14 }, (_, i) => addDays(today, i - 13));
  const byDay = new Map((history.data ?? []).map((d) => [d.day, d.mood]));

  const choose = (m: Mood) => {
    setPicked(m);
    setMood.mutate({ schoolId: ctx.schoolId, mood: m });
  };

  return (
    <Block>
      <Head title={t('care.mood.question')} />
      <div className="-mx-1 grid grid-cols-6 gap-0.5 sm:gap-1">
        {MOODS.map((m, i) => (
          <button
            key={m}
            type="button"
            aria-pressed={mood === m}
            onClick={() => choose(m)}
            className="motion-pop flex min-w-0 flex-col items-center gap-1.5 rounded-[18px] px-0.5 py-2.5 transition-[background-color,transform] duration-300 ease-spring hover:-translate-y-1"
            style={
              {
                '--delay': `${i * 0.05}s`,
                backgroundColor: mood === m ? PAPER : undefined,
              } as CSSProperties
            }
          >
            <span className="sm:hidden">
              <MoodArt mood={m} size={42} />
            </span>
            <span className="hidden sm:block">
              <MoodArt mood={m} size={52} />
            </span>
            <span
              className={`text-[10.5px] leading-4 whitespace-nowrap sm:text-[12.5px] ${mood === m ? 'font-medium' : ''}`}
              style={{ color: mood === m ? INK : INK_SOFT }}
            >
              {t(`care.moods.${m}`)}
            </span>
          </button>
        ))}
      </div>
      {mood && (
        <div
          className="motion-rise mt-3 flex flex-wrap items-center gap-x-3 gap-y-2.5 rounded-[18px] px-4 py-3"
          style={{ backgroundColor: PAPER }}
        >
          <span className="mr-auto text-[14px] leading-5" style={{ color: INK }}>
            {!online
              ? t('care.mood.queued')
              : HARD_MOODS.has(mood)
                ? t('care.mood.hard')
                : t('care.mood.good')}
          </span>
          {HARD_MOODS.has(mood) && (
            <span className="flex flex-wrap gap-2">
              <button type="button" onClick={onCalm} className={pillClass('light', 'sm')}>
                {t('care.actions.calm')}
                <PillIcon icon={WindIcon} size="sm" />
              </button>
              <button type="button" onClick={onWrite} className={pillClass('dark', 'sm')}>
                {t('care.actions.write')}
                <PillIcon icon={PaperPlaneTiltIcon} size="sm" />
              </button>
            </span>
          )}
        </div>
      )}
      <p className="mt-6 mb-2.5 text-[12.5px] font-medium" style={{ color: INK_FAINT }}>
        {t('care.mood.history')}
      </p>
      {(history.data ?? []).length === 0 && !mood ? (
        <p className="text-[14px] leading-5" style={{ color: INK_SOFT }}>
          {t('care.mood.historyEmpty')}
        </p>
      ) : (
        <div className="grid grid-cols-7 gap-y-3 sm:grid-cols-14">
          {days.map((d) => {
            const m = byDay.get(d) ?? (d === today ? mood : undefined);
            return (
              <span
                key={d}
                title={`${dayMonth(d)}${m ? ` · ${t(`care.moods.${m}`)}` : ''}`}
                className="flex flex-col items-center gap-1"
              >
                {m ? (
                  <MoodArt mood={m} size={26} />
                ) : (
                  <span className="size-[26px] rounded-full" style={{ backgroundColor: PAPER }} />
                )}
                <span
                  className="text-[10.5px] leading-3"
                  style={{
                    color: d === today ? INK : INK_FAINT,
                    fontWeight: d === today ? 600 : 400,
                  }}
                >
                  {weekdayShort(d)}
                </span>
              </span>
            );
          })}
        </div>
      )}
      <Private className="mt-5 !text-[13px]">{t('care.mood.saved')}</Private>
    </Block>
  );
}

/** One way to get help: a soft card in its colour, its picture big and quiet behind the words. */
function ActionCard({
  tone,
  art,
  title,
  hint,
  onClick,
}: {
  tone: Grad;
  art: ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative isolate flex min-h-[150px] min-w-0 flex-col justify-end overflow-hidden rounded-[24px] p-4 text-left md:p-5"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {art}
      <span className="flex min-w-0 items-end gap-3">
        <span className="min-w-0 flex-1">
          <span
            className="block text-[16px] leading-[21px] font-medium md:text-[17px]"
            style={{ color: INK }}
          >
            {title}
          </span>
          <span className="mt-0.5 block text-[13px] leading-[18px]">{hint}</span>
        </span>
        <span className="hidden shrink-0 sm:block">
          <span className={ringButton}>
            <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
          </span>
        </span>
      </span>
    </button>
  );
}

/** «Мені погано зараз»: coral across the top, the first thing to find. */
function SosCard({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="group col-span-2 flex min-w-0 items-center gap-4 rounded-[24px] p-4 text-left md:p-5"
      style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
    >
      <span
        className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white md:size-14"
        style={{ color: '#E2572B' }}
      >
        <LifebuoyIcon size={26} weight="fill" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block font-display text-[24px] leading-[28px] font-light tracking-[-0.03em] md:text-[28px] md:leading-[32px]"
          style={{ color: INK }}
        >
          {t('care.actions.sos')}
        </span>
        <span className="mt-0.5 block text-[13.5px] leading-[18px]">
          {t('care.actions.sosHint')}
        </span>
      </span>
      <span className="hidden shrink-0 sm:block">
        <span className={ringButton}>
          <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
        </span>
      </span>
    </button>
  );
}

/** A new thread: what about, and what the psychologist may know. */
function WriteDialog({
  ctx,
  onClose,
  onOpened,
  urgent = false,
}: {
  ctx: StudentContext;
  onClose: () => void;
  onOpened: (id: string) => void;
  urgent?: boolean;
}) {
  const { t } = useTranslation();
  const actions = useCareActions();
  const online = useIsOnline();
  const [topic, setTopic] = useState<CareTopic>(urgent ? 'feelings' : 'friends');
  const [body, setBody] = useState(urgent ? t('care.sos.urgentBody') : '');
  const [who, setWho] = useState<'anonymous' | 'class' | 'name'>('anonymous');
  const [queued, setQueued] = useState(false);
  const band =
    (ctx.child.class?.grade ?? 5) <= 4
      ? '1-4'
      : (ctx.child.class?.grade ?? 5) <= 9
        ? '5-9'
        : '10-11';

  const submit = () => {
    actions.open.mutate(
      {
        schoolId: ctx.schoolId,
        kind: 'talk',
        topic,
        body: body.trim(),
        clientId: crypto.randomUUID(),
        urgent,
        showClass: who !== 'anonymous',
        showName: who === 'name',
      },
      { onSuccess: (id) => onOpened(id) },
    );
    if (!online) setQueued(true);
  };

  if (queued) {
    return (
      <Modal title={t('care.write.title')} onClose={onClose}>
        <p className="text-[15px] leading-6" style={{ color: INK_SOFT }}>
          {t('care.write.queued')}
        </p>
      </Modal>
    );
  }
  return (
    <Modal title={urgent ? t('care.sos.urgent') : t('care.write.title')} onClose={onClose}>
      <div className="flex flex-col gap-5">
        {!urgent && (
          <div>
            <Label>{t('care.write.topic')}</Label>
            <div className="flex flex-wrap gap-1.5">
              {CARE_TOPICS.map((key) => (
                <Choice key={key} active={topic === key} onClick={() => setTopic(key)}>
                  {t(`care.topics.${key}`)}
                </Choice>
              ))}
            </div>
          </div>
        )}
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          maxLength={4000}
          aria-label={t('care.write.title')}
          placeholder={t('care.write.placeholder')}
          className={`${FIELD} resize-none`}
          style={{ color: INK }}
        />
        <div>
          <Label>{t('care.write.who')}</Label>
          <div className="flex flex-wrap gap-1.5">
            <Choice
              active={who === 'anonymous'}
              icon={LockSimpleIcon}
              onClick={() => setWho('anonymous')}
            >
              {t('care.write.anonymous')}
            </Choice>
            <Choice active={who === 'class'} onClick={() => setWho('class')}>
              {t('care.write.showClass')}
            </Choice>
            <Choice active={who === 'name'} onClick={() => setWho('name')}>
              {t('care.write.showName')}
            </Choice>
          </div>
          <p className="mt-2.5 text-[14px] leading-5" style={{ color: INK_SOFT }}>
            {who === 'anonymous'
              ? t('care.write.anonymousHint', { band: t(`care.bands.${band}`) })
              : who === 'class'
                ? t('care.write.showClassHint', { class: ctx.child.class?.label ?? '' })
                : t('care.write.showNameHint')}{' '}
            {who !== 'name' && t('care.write.later')}
          </p>
        </div>
        <div>
          <button
            type="button"
            disabled={!body.trim() || actions.open.isPending}
            onClick={submit}
            className={`${pillClass('dark')} disabled:opacity-35`}
          >
            {t('care.write.send')}
            <PillIcon icon={PaperPlaneTiltIcon} />
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BullyingDialog({
  ctx,
  onClose,
  onOpened,
}: {
  ctx: StudentContext;
  onClose: () => void;
  onOpened: (id: string) => void;
}) {
  const { t } = useTranslation();
  const actions = useCareActions();
  const online = useIsOnline();
  const [aboutSelf, setAboutSelf] = useState(true);
  const [place, setPlace] = useState<(typeof BULLY_PLACES)[number]>('class');
  const [body, setBody] = useState('');
  const [named, setNamed] = useState(false);
  const [queued, setQueued] = useState(false);

  const submit = () => {
    actions.open.mutate(
      {
        schoolId: ctx.schoolId,
        kind: 'bullying',
        topic: 'friends',
        body: body.trim(),
        clientId: crypto.randomUUID(),
        showClass: named,
        showName: named,
        incidentPlace: t(`care.bully.places.${place}`),
        aboutSelf,
      },
      { onSuccess: (id) => onOpened(id) },
    );
    if (!online) setQueued(true);
  };

  return (
    <Modal title={t('care.bully.title')} onClose={onClose}>
      {queued ? (
        <p className="text-[15px] leading-6" style={{ color: INK_SOFT }}>
          {t('care.write.queued')}
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-[15px] leading-6" style={{ color: INK_SOFT }}>
            {t('care.bully.intro')}
          </p>
          <div>
            <Label>{t('care.bully.who')}</Label>
            <div className="flex flex-wrap gap-1.5">
              <Choice active={aboutSelf} onClick={() => setAboutSelf(true)}>
                {t('care.bully.me')}
              </Choice>
              <Choice active={!aboutSelf} onClick={() => setAboutSelf(false)}>
                {t('care.bully.other')}
              </Choice>
            </div>
          </div>
          <div>
            <Label>{t('care.bully.where')}</Label>
            <div className="flex flex-wrap gap-1.5">
              {BULLY_PLACES.map((key) => (
                <Choice key={key} active={place === key} onClick={() => setPlace(key)}>
                  {t(`care.bully.places.${key}`)}
                </Choice>
              ))}
            </div>
          </div>
          <div>
            <Label>{t('care.bully.what')}</Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={4000}
              aria-label={t('care.bully.what')}
              placeholder={t('care.bully.placeholder')}
              className={`${FIELD} resize-none`}
              style={{ color: INK }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Choice active={!named} icon={LockSimpleIcon} onClick={() => setNamed(false)}>
              {t('care.write.anonymous')}
            </Choice>
            <Choice active={named} onClick={() => setNamed(true)}>
              {t('care.write.showName')}
            </Choice>
          </div>
          <p className="text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
            {t('care.bully.routing')}
          </p>
          <div>
            <button
              type="button"
              disabled={!body.trim() || actions.open.isPending}
              onClick={submit}
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {t('care.write.send')}
              <PillIcon icon={PaperPlaneTiltIcon} />
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SosDialog({
  onClose,
  onUrgent,
  onBreathe,
}: {
  onClose: () => void;
  onUrgent: () => void;
  onBreathe: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal title={t('care.sos.title')} onClose={onClose}>
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-6" style={{ color: INK }}>
          {t('care.sos.intro')}
        </p>
        <HelpLines />
        <div
          className="flex flex-col items-start gap-2.5 rounded-[20px] p-4"
          style={{ backgroundImage: grad('lavender'), color: GRAD.lavender.ink }}
        >
          <button type="button" onClick={onUrgent} className={pillClass('dark')}>
            {t('care.sos.urgent')}
            <PillIcon icon={PaperPlaneTiltIcon} />
          </button>
          <p className="text-[13px] leading-[18px]">{t('care.sos.urgentHint')}</p>
        </div>
        <div>
          <button type="button" onClick={onBreathe} className={pillClass('light')}>
            {t('care.sos.breathe')}
            <PillIcon icon={WindIcon} />
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function CalmDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'breathing' | 'grounding'>('breathing');
  return (
    <Modal title={t('care.calm.title')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-1.5">
          <Choice active={tab === 'breathing'} onClick={() => setTab('breathing')}>
            {t('care.calm.breathing')}
          </Choice>
          <Choice active={tab === 'grounding'} onClick={() => setTab('grounding')}>
            {t('care.calm.grounding')}
          </Choice>
        </div>
        {tab === 'breathing' ? <Breathing /> : <Grounding />}
        <p className="text-center text-[12.5px]" style={{ color: INK_FAINT }}>
          {t('care.calm.offline')}
        </p>
      </div>
    </Modal>
  );
}

function MeetDialog({ ctx, onClose }: { ctx: StudentContext; onClose: () => void }) {
  const { t } = useTranslation();
  const slots = useCareMeetingSlots(ctx.schoolId);
  const mine = useMeetings({ guestId: ctx.memberId });
  const book = useBookCareMeeting();
  const { cancel } = useMeetingActions();
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState<string>();
  const now = Date.now();
  const upcoming = (mine.data ?? []).filter((m) => Date.parse(m.endsAt) > now - 30 * 60_000);

  const pick = (slotId: string) =>
    book.mutate(
      { slotId, topic },
      {
        onSuccess: () => setMessage(t('care.meet.booked')),
        onError: (e) =>
          setMessage(
            (e as { message?: string }).message?.includes('already')
              ? t('care.meet.already')
              : t('care.meet.taken'),
          ),
      },
    );

  return (
    <Modal title={t('care.meet.title')} onClose={onClose}>
      <div className="flex flex-col gap-5">
        <div className="rounded-[18px] px-4 py-3" style={{ backgroundColor: PAPER }}>
          <Private>{t('care.meet.notAnonymous')}</Private>
        </div>
        {upcoming.length > 0 && (
          <div>
            <Label>{t('care.meet.mine')}</Label>
            <ul className="flex flex-col gap-2">
              {upcoming.map((m) => (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center gap-3 rounded-[18px] px-4 py-3"
                  style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
                >
                  <span className="mr-auto">
                    <span className="block text-[15px] font-medium" style={{ color: INK }}>
                      {dayMonth(m.startsAt.slice(0, 10))}, {timeOf(m.startsAt)}
                    </span>
                    <span className="block text-[13px]">
                      {m.hostName} ·{' '}
                      {m.format === 'online'
                        ? t('care.meet.online')
                        : (m.place ?? t('care.meet.inPerson'))}
                    </span>
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    {canJoinMeeting(m, now) && (
                      <Link
                        to="/meeting/$meetingId"
                        params={{ meetingId: m.id }}
                        className={pillClass('dark', 'sm')}
                      >
                        {t('care.meet.join')}
                        <PillIcon icon={VideoCameraIcon} size="sm" />
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => cancel.mutate(m.id)}
                      className="inline-flex h-9 items-center rounded-full bg-white px-3.5 text-[13px] font-medium ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6]"
                      style={{ color: INK }}
                    >
                      {t('care.meet.cancel')}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <Label>{t('care.meet.free')}</Label>
          {slots.isPending ? (
            <Spinner />
          ) : (slots.data ?? []).length === 0 ? (
            <EmptyState>{t('care.meet.none')}</EmptyState>
          ) : (
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {(slots.data ?? []).slice(0, 12).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  disabled={book.isPending}
                  onClick={() => pick(s.id)}
                  className="rounded-[16px] px-3.5 py-2.5 text-left transition-colors hover:bg-[#E9E5DC]"
                  style={{ backgroundColor: PAPER }}
                >
                  <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                    {shortDate(s.starts_at.slice(0, 10))}
                  </span>
                  <span
                    className="block font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
                    style={{ color: INK }}
                  >
                    {timeOf(s.starts_at)}
                  </span>
                  <span className="block text-[12px]" style={{ color: INK_FAINT }}>
                    {s.format === 'online'
                      ? t('care.meet.online')
                      : (s.place ?? t('care.meet.inPerson'))}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          maxLength={300}
          aria-label={t('care.meet.topic')}
          placeholder={t('care.meet.topic')}
          className={`${FIELD} h-11 py-0`}
          style={{ color: INK }}
        />
        {message && (
          <p
            className="rounded-[16px] px-4 py-3 text-[14px] font-medium"
            style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
          >
            {message}
          </p>
        )}
      </div>
    </Modal>
  );
}

function ThreadRow({
  thread,
  active,
  onClick,
}: {
  thread: CareThread;
  active: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const look = threadLook(thread);
  const status = thread.unreadForStudent
    ? t('care.threads.unread')
    : thread.status === 'closed'
      ? t('care.threads.closed')
      : thread.lastFromStudent
        ? t('care.threads.waiting')
        : t('care.threads.answered');
  return (
    <button
      type="button"
      aria-current={active || undefined}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-[18px] px-3 py-2.5 text-left transition-colors ${active ? '' : 'hover:bg-[#FAF9F6]'}`}
      style={{ backgroundColor: active ? PAPER : undefined }}
    >
      <IconDot icon={look.icon} tone={look.tone} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] leading-5 font-medium" style={{ color: INK }}>
          {threadTitle(t, thread)}
        </span>
        <span
          className={`block truncate text-[13px] leading-[18px] ${thread.unreadForStudent ? 'font-medium' : ''}`}
          style={{ color: thread.unreadForStudent ? GRAD.coral.ink : INK_SOFT }}
        >
          {status} · {shortDate(thread.lastMessageAt.slice(0, 10))}
        </span>
      </span>
      {thread.unreadForStudent && (
        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: '#FF6B3D' }} />
      )}
    </button>
  );
}

/**
 * «Турбота» for a student: the mood of the day, five ways to get help, and the conversations
 * with the psychologist. The promise «only you and the psychologist» is said first.
 */
export function StudentCare({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { thread?: string; open?: string };
  const navigate = useNavigate();
  const threads = useCareThreads(ctx.schoolId);
  const actions = useCareActions();
  const [selected, setSelected] = useState<string | undefined>(search.thread);
  const [dialog, setDialog] = useState<Dialog>(
    search.open === 'calm' || search.open === 'sos' ? search.open : null,
  );
  const [urgent, setUrgent] = useState(false);
  const [revealWhat, setRevealWhat] = useState<{ showClass: boolean; showName: boolean }>();

  useEffect(() => {
    if (search.thread) setSelected(search.thread);
  }, [search.thread]);

  const list = threads.data ?? [];
  const current = list.find((th) => th.id === selected) ?? list[0];
  const opened = (id: string) => {
    setDialog(null);
    setSelected(id);
    void navigate({ to: '/care', search: { thread: id } });
  };
  const write = () => {
    setUrgent(false);
    setDialog('write');
  };

  const reveal = (what: { showClass: boolean; showName: boolean }) => {
    if (!current) return;
    if (what.showName) {
      setRevealWhat(what);
      setDialog('reveal');
    } else {
      actions.reveal.mutate({ threadId: current.id, ...what });
    }
  };

  const ghost = 'pointer-events-none absolute -top-5 -right-5 -z-10';
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('care.title')}
        sub={
          <Private className="max-w-[640px] !text-[15px] !leading-[22px]">
            {t('care.promise')}
          </Private>
        }
      />

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <div className="grid grid-cols-2 gap-3">
          <SosCard onClick={() => setDialog('sos')} />
          <ActionCard
            tone="lavender"
            art={<TaskArt kind="soon" tone="lavender" size={120} ghost className={ghost} />}
            title={t('care.actions.write')}
            hint={t('care.actions.writeHint')}
            onClick={write}
          />
          <ActionCard
            tone="mint"
            art={<EventArt kind="celebration" tone="mint" size={120} ghost className={ghost} />}
            title={t('care.actions.calm')}
            hint={t('care.actions.calmHint')}
            onClick={() => setDialog('calm')}
          />
          <ActionCard
            tone="apricot"
            art={<TaskArt kind="ack" tone="apricot" size={120} ghost className={ghost} />}
            title={t('care.actions.bullying')}
            hint={t('care.actions.bullyingHint')}
            onClick={() => setDialog('bullying')}
          />
          <ActionCard
            tone="sky"
            art={<EventArt kind="meeting" tone="sky" size={120} ghost className={ghost} />}
            title={t('care.actions.meet')}
            hint={t('care.actions.meetHint')}
            onClick={() => setDialog('meet')}
          />
        </div>
        <MoodCard ctx={ctx} onCalm={() => setDialog('calm')} onWrite={write} />
      </div>

      <Block>
        <Head title={t('care.threads.title')} count={list.length}>
          {list.length > 0 && (
            <>
              <button
                type="button"
                onClick={write}
                aria-label={t('care.actions.write')}
                title={t('care.actions.write')}
                className={`${ringButton} sm:hidden`}
              >
                <PaperPlaneTiltIcon size={16} weight="bold" aria-hidden />
              </button>
              <span className="hidden sm:block">
                <button type="button" onClick={write} className={pillClass('light', 'sm')}>
                  {t('care.actions.write')}
                  <PillIcon icon={PaperPlaneTiltIcon} size="sm" />
                </button>
              </span>
            </>
          )}
        </Head>
        {threads.isPending ? (
          <Spinner />
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <EventArt kind="meeting" tone="lavender" size={88} />
            <p
              className="mt-3 max-w-md font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
              style={{ color: INK }}
            >
              {t('care.threads.empty')}
            </p>
            <p className="mt-1 max-w-sm text-[14px]" style={{ color: INK_SOFT }}>
              {t('care.noAi')}
            </p>
            <button type="button" onClick={write} className={`${pillClass('dark')} mt-5`}>
              {t('care.actions.write')}
              <PillIcon icon={PaperPlaneTiltIcon} />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-6">
            <div className="flex flex-col gap-1">
              {list.map((th) => (
                <ThreadRow
                  key={th.id}
                  thread={th}
                  active={th.id === current?.id}
                  onClick={() => setSelected(th.id)}
                />
              ))}
              <p className="mt-3 px-3 text-[12.5px] leading-[17px]" style={{ color: INK_FAINT }}>
                {t('care.noAi')}
              </p>
            </div>
            {current && (
              <ThreadView key={current.id} thread={current} side="student" onReveal={reveal} />
            )}
          </div>
        )}
      </Block>

      {dialog === 'write' && (
        <WriteDialog ctx={ctx} urgent={urgent} onClose={() => setDialog(null)} onOpened={opened} />
      )}
      {dialog === 'bullying' && (
        <BullyingDialog ctx={ctx} onClose={() => setDialog(null)} onOpened={opened} />
      )}
      {dialog === 'sos' && (
        <SosDialog
          onClose={() => setDialog(null)}
          onUrgent={() => {
            setUrgent(true);
            setDialog('write');
          }}
          onBreathe={() => setDialog('calm')}
        />
      )}
      {dialog === 'calm' && <CalmDialog onClose={() => setDialog(null)} />}
      {dialog === 'meet' && <MeetDialog ctx={ctx} onClose={() => setDialog(null)} />}
      {dialog === 'reveal' && current && revealWhat && (
        <Modal title={t('care.thread.revealName')} onClose={() => setDialog(null)}>
          <p className="text-[15px] leading-6" style={{ color: INK }}>
            {t('care.thread.revealConfirm')}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                actions.reveal.mutate({ threadId: current.id, ...revealWhat });
                setDialog(null);
              }}
              className={pillClass('dark')}
            >
              {t('care.thread.revealName')}
              <PillIcon icon={CaretRightIcon} />
            </button>
            <button
              type="button"
              onClick={() => setDialog(null)}
              className="inline-flex h-11 items-center rounded-full bg-white px-4.5 text-[14px] font-medium ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6]"
              style={{ color: INK }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
